import type { RefObject } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export interface DismissableLayerOptions {
	/** Listen only while the layer is open. */
	enabled: boolean;
	/** The layer's own root element: where the returned handlers are spread. */
	rootRef: RefObject<HTMLElement | null>;
	/**
	 * A pointerdown started outside the layer: outside its root element and
	 * outside everything rendered from it, portalled menus included. Receives
	 * the target so the caller can exempt more, such as its trigger.
	 */
	onPointerDownOutside?: (target: Node) => void;
	/** Escape was pressed and nothing nested in the layer claimed it. */
	onEscapeKeyDown?: () => void;
}

/** Spread onto the layer's root element. */
export interface DismissableLayerProps {
	onPointerDownCapture: () => void;
	onKeyDownCapture: () => void;
}

/**
 * Outside-click and Escape dismissal for an overlay that can hold other
 * overlays (PD-16154).
 *
 * Deciding "outside" by DOM containment breaks as soon as the overlay holds a
 * select, a menu or a date picker: they portal their lists to <body>, so a
 * press on an option lands outside the overlay in the DOM and the overlay
 * closed on the very press that chose the option. React events travel along
 * the component tree through portals, so the capture handlers returned here,
 * spread on the overlay's root, see every press and keystroke that starts
 * inside the overlay or inside anything opened from it. They set a flag the
 * document listeners read; a document-level capture listener clears it at
 * the start of every event, so a value cannot outlive the event that set it.
 *
 * Escape belongs to the innermost layer. It is left alone when:
 *   - a nested control already handled it (`defaultPrevented`). react-select
 *     marks the Escape that closes its menu this way; Headless UI's Listbox,
 *     Menu and Combobox stop it outright, so it never reaches the document.
 *     This is also the convention Headless UI's Dialog follows, which is why
 *     the layer marks the Escape it acts on: a popover opened from a Modal
 *     closes without the Modal closing too.
 *   - it came from a layer nested in this one and portalled out of it (a
 *     popover inside a popover): in this layer's React tree, but not inside
 *     its own element.
 *
 * Listeners attach on the next tick so the press that opened the layer does
 * not immediately count as an outside press.
 */
export function useDismissableLayer({
	enabled,
	rootRef,
	onPointerDownOutside,
	onEscapeKeyDown,
}: DismissableLayerOptions): DismissableLayerProps {
	const pointerDownInsideRef = useRef(false);
	const keyDownInsideRef = useRef(false);
	// The latest callbacks, so the document listeners attach once per open
	// rather than on every render (callers pass inline functions).
	const handlersRef = useRef({ onPointerDownOutside, onEscapeKeyDown });
	useLayoutEffect(() => {
		handlersRef.current = { onPointerDownOutside, onEscapeKeyDown };
	});

	useEffect(() => {
		if (!enabled) {
			return undefined;
		}

		const isInsideRoot = (target: EventTarget | null): boolean =>
			target instanceof Node && rootRef.current?.contains(target) === true;

		const resetPointerDown = (): void => {
			pointerDownInsideRef.current = false;
		};
		const resetKeyDown = (): void => {
			keyDownInsideRef.current = false;
		};

		const handlePointerDown = (event: PointerEvent): void => {
			const startedInsideReactTree = pointerDownInsideRef.current;
			pointerDownInsideRef.current = false;
			const target = event.target;
			if (
				startedInsideReactTree ||
				!(target instanceof Node) ||
				isInsideRoot(target)
			) {
				return;
			}
			handlersRef.current.onPointerDownOutside?.(target);
		};

		const handleKeyDown = (event: KeyboardEvent): void => {
			const startedInsideReactTree = keyDownInsideRef.current;
			keyDownInsideRef.current = false;
			if (event.key !== 'Escape' || event.defaultPrevented) {
				return;
			}
			if (startedInsideReactTree && !isInsideRoot(event.target)) {
				return;
			}
			const onEscape = handlersRef.current.onEscapeKeyDown;
			if (onEscape == null) {
				return;
			}
			event.preventDefault();
			onEscape();
		};

		const timeoutId = setTimeout(() => {
			document.addEventListener('pointerdown', resetPointerDown, true);
			document.addEventListener('pointerdown', handlePointerDown);
			document.addEventListener('keydown', resetKeyDown, true);
			document.addEventListener('keydown', handleKeyDown);
		}, 0);

		return (): void => {
			clearTimeout(timeoutId);
			document.removeEventListener('pointerdown', resetPointerDown, true);
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', resetKeyDown, true);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [enabled, rootRef]);

	const onPointerDownCapture = useCallback((): void => {
		pointerDownInsideRef.current = true;
	}, []);
	const onKeyDownCapture = useCallback((): void => {
		keyDownInsideRef.current = true;
	}, []);

	return { onPointerDownCapture, onKeyDownCapture };
}
